import type { FluxErrorEntityErrorCodes } from '../types.js';

export const ReviewErrorCodes = {
  ReviewAlreadyFinalized: {
    code: 'review/already-finalized',
    explanation:
      'Você não pode alterar os revisores de uma revisão que já foi finalizada. Revisões finalizadas, ou seja, aprovadas ou rejeitadas, não podem ter seus revisores alterados.',
    localizations: {},
    message: 'Não é possível alterar aprovadores de uma revisão já finalizada.',
    supportingDocs: [
      {
        name: 'Revisões',
        path: '/wiki/docs/cards/approvals/intro',
      },
      {
        name: 'Pós Revisão',
        path: '/wiki/docs/cards/approvals/post-review',
      },
    ],
  },
  ReviewCommentRequired: {
    code: 'review/comment-required',
    explanation:
      'Um comentário é obrigatório para aprovar ou rejeitar esta revisão. Revisões podem ser configuradas para exigir comentários dos revisores.',
    localizations: {},
    message: 'O comentário é obrigatório para aprovar ou rejeitar.',
    supportingDocs: [
      {
        name: 'Revisando um processo',
        path: '/wiki/docs/cards/approvals/review-card#configuração-avançada-comentário-obrigatório-para-aprovar',
      },
    ],
  },
  ReviewNotFound: {
    code: 'review/not-found',
    explanation:
      'A revisão que você está tentando acessar não foi encontrada. Ela pode ter sido concluída, cancelada ou removida.',
    localizations: {},
    message: 'A aprovação solicitada não foi encontrada.',
  },
  ReviewRequestFirstReviewerNotFound: {
    code: 'review/request-first-reviewer-not-found',
    explanation: 'O primeiro revisor para esta solicitação de revisão não foi encontrado.',
    localizations: {},
    message: 'Não foi possível encontrar o primeiro revisor.',
  },
  ReviewRequestIncompleteReviewers: {
    code: 'review/request-incomplete-reviewers',
    explanation:
      'Não há revisores suficientes para concluir esta solicitação de revisão. Revisões podem exigir um número mínimo de aprovadores para aprovação.',
    localizations: {},
    message: 'Não há aprovadores suficientes configurados para concluir esta solicitação. ',
    supportingDocs: [
      {
        name: 'Atribuindo um revisor',
        path: '/wiki/docs/cards/approvals/assign-reviewer',
      },
    ],
  },
  ReviewRequestNotAssigned: {
    code: 'review/request-not-assigned',
    explanation:
      'Você não está atribuído a este card e, portanto, não pode solicitar uma revisão. Apenas usuários atribuídos podem solicitar revisões para seus cards.',
    localizations: {},
    message: 'Você não está atribuído a este card e não pode solicitar uma revisão.',
    supportingDocs: [
      {
        name: 'Responsáveis pelo Card',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  ReviewRequestNotFound: {
    code: 'review/request-not-found',
    explanation: 'A solicitação de revisão que você está tentando acessar não foi encontrada.',
    localizations: {},
    message: 'A solicitação de aprovação não foi encontrada.',
  },
  ReviewRequestNotOwner: {
    code: 'review/request-not-owner',
    explanation:
      'Você não é o proprietário desta solicitação de revisão e, portanto, não pode gerenciá-la. Apenas quem requisitou a revisão pode gerenciá-la, ou seja, adicionar ou remover revisores, cancelar a solicitação, etc.',
    localizations: {},
    message: 'Apenas o proprietário pode gerenciar esta solicitação de aprovação.',
  },
  ReviewRequestPending: {
    code: 'review/request-pending',
    explanation:
      'Este card já possui uma solicitação de revisão pendente. Processos só podem ter uma revisão pendente por vez. Aguarde a conclusão da aprovação atual pelos aprovadores designados ou cancele a solicitação existente antes de criar uma nova.',
    localizations: {},
    message: 'Este processo já possui uma solicitação de aprovação pendente.',
    supportingDocs: [
      {
        name: 'Aprovações',
        path: '/wiki/docs/cards/approvals/intro',
      },
    ],
  },
  ReviewRequestSelfReviewer: {
    code: 'review/request-self-reviewer',
    explanation: 'Você não pode revisar sua própria solicitação de revisão.',
    localizations: {},
    message: 'Você não pode revisar sua própria solicitação.',
  },
  ReviewRequestStageDeleted: {
    code: 'review/request-stage-deleted',
    explanation:
      'A etapa associada a esta solicitação de revisão foi arquivada. Revisões são vinculadas a etapas específicas no fluxo.',
    localizations: {},
    message: 'A etapa desta aprovação foi arquivada.',
  },
  ReviewRequestStageNotFound: {
    code: 'review/request-stage-not-found',
    explanation: 'A etapa associada a esta solicitação de revisão não foi encontrada.',
    localizations: {},
    message: 'Etapa da aprovação não encontrada.',
  },
  ReviewRequestUserNotFound: {
    code: 'review/request-user-not-found',
    explanation: 'O usuário associado a esta solicitação de revisão não foi encontrado.',
    localizations: {},
    message: 'Usuário relacionado à aprovação não encontrado.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'review'>;

export const ReviewerErrorCodes = {
  ReviewerCannotApprove: {
    code: 'reviewer/cannot-approve',
    explanation:
      'Você não pode aprovar esta solicitação de revisão no momento. Isso pode acontecer caso a aprovação seja sequencial e não seja sua vez de aprovar.',
    localizations: {},
    message: 'Você não pode aprovar essa solicitação no momento. Tentar novamente mais tarde.',
    supportingDocs: [
      {
        name: 'Atribuindo um revisor',
        path: '/wiki/docs/cards/approvals/assign-reviewer',
      },
    ],
  },
  ReviewerCannotReject: {
    code: 'reviewer/cannot-reject',
    explanation:
      'Você não pode rejeitar esta solicitação de revisão no momento. Isso pode acontecer caso a aprovação seja sequencial e não seja sua vez de aprovar.',
    localizations: {},
    message: 'Você não pode rejeitar essa solicitação no momento. Tentar novamente mais tarde.',
  },
  ReviewerDuplicateReviewer: {
    code: 'reviewer/duplicate-reviewer',
    explanation:
      'O revisor que você está tentando adicionar a esta solicitação de revisão já foi adicionado.',
    localizations: {},
    message: 'Este revisor já foi adicionado à solicitação.',
  },
  ReviewerInvalidStatus: {
    code: 'reviewer/invalid-status',
    explanation:
      'Você não pode alterar um revisor que já revisou a solicitação. Apenas revisores pendentes podem ser alterados.',
    localizations: {},
    message: 'Não é possível alterar um aprovador que já revisou a solicitação.',
  },
  ReviewerNotFound: {
    code: 'reviewer/not-found',
    explanation: 'O revisor que você está tentando acessar não foi encontrado.',
    localizations: {},
    message: 'O aprovador especificado não foi encontrado.',
  },
  ReviewerPositionNotFound: {
    code: 'reviewer/position-not-found',
    explanation: 'A posição do revisor que você está tentando acessar não foi encontrada.',
    localizations: {},
    message: 'Posição do revisor não encontrada.',
  },
  ReviewerRequestCompleted: {
    code: 'reviewer/request-completed',
    explanation:
      'Esta solicitação de revisão já foi concluída e não pode ser modificada. Revisões concluídas são aquelas que foram aprovadas ou rejeitadas.',
    localizations: {},
    message: 'Esta solicitação de aprovação já foi concluída.',
  },
  ReviewerRequiredFieldsEmpty: {
    code: 'reviewer/required-fields-empty',
    explanation:
      'Um ou mais campos obrigatórios no formulário do revisor não foram preenchidos. Revisões podem ter campos obrigatórios configurados.',
    localizations: {},
    message: 'O formulário não foi preenchido corretamente. Verifique os campos obrigatórios.',
    supportingDocs: [
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
      {
        name: 'Revisando um processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
  ReviewerStatusMismatch: {
    code: 'reviewer/status-mismatch',
    explanation: 'O status do revisor não permite que esta ação seja executada.',
    localizations: {},
    message: 'O status do revisor não permite esta ação.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'reviewer'>;
